import { CMAPS_PATH } from 'app/constants/PdfSettings';
import { MainThreadWorkerLoaderGlobalInstance } from 'app/services/pdf/PdfWorkerLoader';
import { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/display/api';

type Source = { url: string } | { data: Uint8Array };

type Init =
  Source &
  {
    readonly scale?: number;
    readonly start?: number;
    readonly end?: number;
  };

type OnContinue = (next: () => void) => void;

/**
 * Safe subset of RenderTask. Unfortunately becuase it has `then`
 * method, if we do `await renderTask` promise resolution will
 * await until the task itself is done, making it hard to access the
 * `cancel` method.
 *
 * At the moment RenderTask type is not exported from the pdfjs-dist library but
 * in the near future the types can be derived or taken from there.
 */
export interface IRenderTaskSafeSubset {
  promise: Promise<void>;
  cancel(): void;
}

export const loadPdf = async ({
  scale = 1,
  start = -Infinity,
  end = Infinity,
  ...src
}: Init): Promise<PDF> => {
  const workerloadedOnMainThread =
    await MainThreadWorkerLoaderGlobalInstance.loadMainThreadWorkerInProblematicBrowsers();

  const PDFJS = await import('app/services/pdf/pdfjs');

  // Maintain a reference of the worker that will be used by PDFJS and one that should be terminated
  // when cleaning up the documents.
  let worker: Worker | null = null;
  let pdfWorker: unknown = undefined;

  // If the main thread worker is not being used, instantiate a new worker.
  if (!workerloadedOnMainThread) {
    const PDFJsWorker = await (await import('worker-component!./pdfjs.worker')).default;
    worker = new PDFJsWorker();
    pdfWorker = PDFJS.PDFWorker.fromPort({ port: worker });
  }

  const pdfDocumentLoadingTask = PDFJS.getDocument({
    ...src,
    cMapUrl: CMAPS_PATH,
    cMapPacked: true,
    worker: pdfWorker,
  });

  const doc = await pdfDocumentLoadingTask.promise;

  const pages: PDFPageProxy[] = [];

  try {
    for (
      let i = Math.max(start, 1);
      i <= Math.min(end, doc.numPages);
      ++i
    ) {
      pages.push(await doc.getPage(i));
    }
  } catch (error) {
    // Reuse implementation from PDF class to cleanup pages created so far and the doc
    new PDF(doc, worker, pages, scale).cleanup();
    throw error;
  }

  return new PDF(doc, worker, pages, scale);
};

export class PDF {
  public constructor(
    private readonly doc: PDFDocumentProxy,
    private worker: Worker | null,
    private readonly pages: readonly PDFPageProxy[],
    private readonly scale: number,
  ) {}

  public cleanup() {
    for (const page of this.pages) {
      page.cleanup(true);
    }

    this.doc.cleanup();
    this.doc.destroy();
    this.worker?.terminate();
    this.worker = null;
  }

  public get pageCount() {
    return this.pages.length;
  }

  /**
   * Returns size for a page. This has the `scale` applied and  is rounded to
   * nearest pixels.
   */
  public getPageSize(pageNum: number) {
    const { width, height } = this.getPage(pageNum).getViewport({ scale: this.scale });

    return {
      width: Math.round(width),
      height: Math.round(height),
    };
  }

  /**
   * Starts a rendering process and returns a safe (non-thenable) subset of a
   * `RenderTask`.
   */
  public renderPage(
    pageNum: number,
    /**
     * Canvas 2D rendering context. Should have a size returned by
     * `getPageSize` for this page.
     */
    canvasContext: CanvasRenderingContext2D,
    /**
     * Passing `onContinue` allows to suspend rendering until the
     * callback passed to `onContinue` is called.
     */
    onContinue?: OnContinue,
  ): IRenderTaskSafeSubset {
    const page = this.getPage(pageNum);

    const task = page.render({
      canvasContext,
      viewport: page.getViewport({ scale: this.scale }),
    });

    if (onContinue) {
      task.onContinue = onContinue;
    }

    return {
      promise: task.promise,
      cancel: () => task.cancel(),
    };
  }

  private getPage(pageNum: number): PDFPageProxy {
    const page = this.pages[pageNum - 1];

    if (page) {
      return page;
    } else {
      throw new Error(`Page ${pageNum} doesn't exist`);
    }
  }
}
